import React                    from 'react';
import { observer }             from 'mobx-react-lite';
import View                     from '@widesk-ui/components/View';
import Alert                    from '@widesk-ui/components/Alert';
import Button                   from '@widesk-ui/components/Button';
import Input                    from '@widesk-ui/components/form/Input';
import { LeftOutlined }         from '@ant-design/icons';
import { RightOutlined }        from '@ant-design/icons';
import useDesignTokens          from '@/hooks/useDesignTokens';
import { arbitrationOf }        from '@/TokamapEditor/helpers/planDoubts';
import { doubtKey }             from '@/TokamapEditor/helpers/planDoubts';
import planImportStore          from '@/TokamapEditor/planImport/planImportStore';

const PlanDoubtCard = observer((props: { onShow: () => void }) => {
	const tokens = useDesignTokens();
	const { detection, arbitrations, corrections, currentDoubtIndex, activeDoubtIndexes } = planImportStore;
	const doubt = null === currentDoubtIndex ? undefined : detection?.doubts[currentDoubtIndex];
	const position = null === currentDoubtIndex ? -1 : activeDoubtIndexes.indexOf(currentDoubtIndex);

	const go = (offset: number) => planImportStore.stepDoubt(offset);

	React.useEffect(() => {
		if (!doubt) return;

		const onKey = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
			if (event.metaKey || event.ctrlKey || event.altKey) return;

			const key = event.key.toLowerCase();
			if ('g' === key && doubt.keepable) planImportStore.arbitrate(doubt, 'kept');
			else if ('e' === key) planImportStore.arbitrate(doubt, 'discarded');
			else if ('arrowright' === event.key) go(1);
			else if ('arrowleft' === event.key) go(-1);
			else return;

			event.preventDefault();
		};

		window.addEventListener('keydown', onKey);

		return () => window.removeEventListener('keydown', onKey);
	}, [doubt]);

	if (!activeDoubtIndexes.length) {
		return <Alert type="success" showIcon message="Rien à vérifier sur ce plan" />;
	}

	if (!doubt) return null;

	const key = doubtKey(doubt);
	const decision = arbitrationOf(doubt, arbitrations);
	const correctable = null !== doubt.suggestion || 'no_number' === doubt.kind;

	return (
		<View
			gap={10}
			style={{
				padding: 12,
				borderRadius: 8,
				border: `1px solid color-mix(in srgb, ${tokens.color.warning} 40%, transparent)`,
				background: `color-mix(in srgb, ${tokens.color.warning} 8%, transparent)`,
			}}
		>
			<View row gap={8} style={{ alignItems: 'center' }}>
				<View
					center
					size={12}
					style={{
						flex: '0 0 auto',
						width: 22,
						height: 22,
						borderRadius: '50%',
						fontWeight: 600,
						background: tokens.color.primary,
						color: tokens.surface.card,
					}}
				>
					{position + 1}
				</View>
				<View flex size={14} style={{ fontWeight: 600 }}>{doubt.label}</View>
				<Button type="text" size="small" icon={<LeftOutlined />} title="Précédent (←)" onClick={() => go(-1)} />
				<View size={12} color={tokens.text.secondary}>{position + 1} / {activeDoubtIndexes.length}</View>
				<Button type="text" size="small" icon={<RightOutlined />} title="Suivant (→)" onClick={() => go(1)} />
			</View>

			<View size={13}>{doubt.reason}</View>

			{correctable && (
				<Input
					placeholder="Numéro du stand"
					value={corrections[key] ?? doubt.suggestion ?? ''}
					onChange={(value: unknown) => planImportStore.correct(doubt, String(value ?? ''))}
				/>
			)}

			<View row gap={8} style={{ alignItems: 'center' }}>
				{doubt.keepable && (
					<Button
						size="small"
						type={'kept' === decision ? 'primary' : 'default'}
						title="Raccourci : G"
						onClick={() => planImportStore.arbitrate(doubt, 'kept')}
					>
						Garder
					</Button>
				)}
				<Button
					size="small"
					danger={'discarded' === decision}
					type={'discarded' === decision ? 'primary' : 'default'}
					title="Raccourci : E"
					onClick={() => planImportStore.arbitrate(doubt, 'discarded')}
				>
					Écarter
				</Button>
				<View flex />
				<Button size="small" type="link" style={{ padding: 0 }} onClick={props.onShow}>Voir sur le plan</Button>
			</View>
		</View>
	);
});

export default PlanDoubtCard;
